/**
 * Swipe for every slider on the site (centres, packages, treatments, photo
 * pop-ups). A slider is found by its own controls: a previous and a next arrow
 * button, or failing that a row of dots. A mostly sideways swipe of at least
 * 40px inside it presses the matching control, so each slider keeps its own
 * rules (looping, autoplay, how far a step moves). Scrolling the page up or
 * down and tapping a photo work as before.
 */
const PREV_ICON = /\blucide-(chevron|arrow)-left\b/;
const NEXT_ICON = /\blucide-(chevron|arrow)-right\b/;
const STOP_TAGS = new Set(["SECTION", "MAIN", "BODY", "HEADER", "FOOTER", "NAV", "ARTICLE"]);

const usable = (el: HTMLElement) => el.getClientRects().length > 0 && !(el as HTMLButtonElement).disabled;

/** The button's words, leaving out screen-reader-only text. */
function words(b: HTMLElement) {
  let text = b.textContent ?? "";
  b.querySelectorAll(".sr-only").forEach((s) => (text = text.replace(s.textContent ?? "", "")));
  return text.trim();
}

/** "prev" / "next" for an arrow button, null for anything else. */
function arrowKind(b: HTMLElement): "prev" | "next" | null {
  const label = `${b.getAttribute("aria-label") ?? ""} ${b.getAttribute("title") ?? ""}`;
  if (/\b(prev|previous)\b/i.test(label)) return "prev";
  if (/\bnext\b/i.test(label)) return "next";
  if (words(b)) return null; // a button with words ("Next step", "Know more") is not a slider arrow
  const icon = b.querySelector("svg")?.getAttribute("class") ?? "";
  if (PREV_ICON.test(icon)) return "prev";
  if (NEXT_ICON.test(icon)) return "next";
  return null;
}

function arrows(box: Element) {
  let prev: HTMLElement | null = null;
  let next: HTMLElement | null = null;
  box.querySelectorAll<HTMLElement>("button, [role='button']").forEach((b) => {
    if (!usable(b)) return;
    const kind = arrowKind(b);
    if (kind === "prev" && !prev) prev = b;
    if (kind === "next" && !next) next = b;
  });
  return prev && next ? { prev: prev as HTMLElement, next: next as HTMLElement } : null;
}

/** A row of two or more empty buttons where exactly one looks different (the current slide). */
function dots(box: Element) {
  const rows = new Map<Element, HTMLElement[]>();
  box.querySelectorAll<HTMLElement>("button").forEach((b) => {
    if (!usable(b) || words(b) || b.querySelector("svg, img") || !b.parentElement) return;
    rows.set(b.parentElement, [...(rows.get(b.parentElement) ?? []), b]);
  });
  for (const row of rows.values()) {
    if (row.length < 2) continue;
    const counts = new Map<string, number>();
    row.forEach((b) => counts.set(b.className, (counts.get(b.className) ?? 0) + 1));
    const odd = row.filter((b) => counts.get(b.className) === 1);
    if (odd.length === 1 && (row.length > 2 || counts.size === 2)) return { row, current: row.indexOf(odd[0]) };
  }
  return null;
}

/** True when something between the finger and the slider scrolls sideways by itself. */
function scrollsSideways(from: Element, to: Element) {
  for (let el: Element | null = from; el && el !== to; el = el.parentElement) {
    if (el.scrollWidth > el.clientWidth + 2 && /auto|scroll/.test(getComputedStyle(el).overflowX)) return true;
  }
  return false;
}

function swipe(target: Element, forward: boolean) {
  if (target.closest("button, input, textarea, select, [role='slider'], [aria-roledescription='carousel']")) return;
  for (let box: Element | null = target; box; box = box.parentElement) {
    const found = arrows(box);
    if (found) {
      if (!scrollsSideways(target, box)) (forward ? found.next : found.prev).click();
      return;
    }
    const row = dots(box);
    if (row) {
      if (!scrollsSideways(target, box)) {
        const n = row.row.length;
        row.row[(row.current + (forward ? 1 : n - 1)) % n].click();
      }
      return;
    }
    if (STOP_TAGS.has(box.tagName) || box.getAttribute("role") === "dialog") return;
  }
}

export function startSliderSwipe() {
  let start: { x: number; y: number; target: Element } | null = null;
  document.addEventListener(
    "touchstart",
    (e) => {
      const t = e.touches[0];
      start = e.touches.length === 1 && t.target instanceof Element ? { x: t.clientX, y: t.clientY, target: t.target } : null;
    },
    { passive: true }
  );
  document.addEventListener(
    "touchend",
    (e) => {
      const s = start;
      start = null;
      if (!s?.target) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      swipe(s.target, dx < 0);
    },
    { passive: true }
  );
}
